import { frameId } from "../assets/manifest.ts";
import { FOOTPRINT_DEFAULT_CELL } from "../compile/footprint-cell.ts";
import { footprintOpens, minCoverForCell, sameFootprint, type PropFootprint } from "../compile/footprint.ts";
import { footprintFromCells, type FootprintOverride } from "../compile/footprint-override.ts";
import type { FrameMarks } from "../compile/stand-map.ts";
import { marksAtCell, standCellsOf } from "./stand-paint.ts";
import { tilesetUses, type TilesetUse } from "../document/tileset-change.ts";
import type { WorldDocument } from "../document/types.ts";

/**
 * Copying one frame's footprint across a plays-once strip (ADR-040 amendment of 2026-09-24, R-0020). A
 * once strip keeps a footprint per frame because a thing's states rest on different frames of it, so the
 * copy writes the strip's other frames except one that a placed prop or a behaviour state rests on and
 * whose footprint or ground flag it would change: writing that frame would change what the thing blocks
 * or how it draws, as copying a closed gate's span over the frame its open state rests on would.
 */

/**
 * Something that rests on a frame, so the frame's footprint is what it blocks by and its ground flag how
 * it draws: a placed prop on its own frame, or a behaviour state on the frame its behaviour names
 * (ADR-053 decision 3). A transition's animation rests nothing on its frames, since collision follows the
 * state and never the frames a transition plays (ADR-058 amendment 1).
 */
export type FrameRest = Extract<TilesetUse, { readonly kind: "prop" | "state" }>;

/**
 * A frame's footprint as the footprint panel shows and writes it: the collision cells it blocks on the
 * frame's own grid as `[column, rowsUp]` from the frame's bottom-left (`footprintBlocks`), the footprint
 * play stamps (undefined when the frame has none), whether it draws under players, whether it then lies flat among
 * props (`amongProps`, ADR-040's amendment of 2026-10-06; absent when the look says nothing of it), and the Behind and In front
 * cells painted on it (ADR-132), absent for a frame with none.
 */
export interface FootprintLook {
  readonly cells: readonly (readonly [number, number])[];
  readonly footprint: PropFootprint | undefined;
  readonly ground: boolean;
  readonly amongProps?: boolean | undefined;
  readonly marks?: FrameMarks | undefined;
}

/**
 * A frame the copy leaves as it is: its id, its place in the strip, and what rests on it.
 */
export interface KeptFrame {
  readonly frame: string;
  readonly index: number;
  readonly rests: readonly FrameRest[];
}

/**
 * What a copy writes and what it leaves, each in strip order.
 */
export interface FootprintCopyPlan {
  readonly write: readonly string[];
  readonly kept: readonly KeptFrame[];
}

/**
 * The placed props and behaviour states of a world that rest on each frame, in document order: the frames whose
 * footprint something is stamped by. The footprint panel treats a plays-once frame missing here as a draft
 * (ADR-040 amendment of 2026-10-03, P1.6-T74).
 *
 * @param world The document.
 * @returns Frame id to what rests on it; a frame nothing rests on is absent.
 * @throws Error when a tile cell indexes past the world's tilesets.
 */
export function frameRests(world: WorldDocument): Map<string, FrameRest[]> {
  const out = new Map<string, FrameRest[]>();
  for (const use of tilesetUses(world)) {
    if (use.kind !== "prop" && use.kind !== "state") {
      continue;
    }
    const id = frameId(use.tileset, null, use.tile);
    const rests = out.get(id);
    if (rests === undefined) {
      out.set(id, [use]);
    } else {
      rests.push(use);
    }
  }
  return out;
}

/**
 * Whether two frames' marks say the same: both say nothing, or they hold the same cells counted in one cell.
 *
 * @param a One frame's marks, or undefined.
 * @param b The other's.
 * @returns True when they match.
 */
function sameMarks(a: FrameMarks | undefined, b: FrameMarks | undefined): boolean {
  const empty = (marks: FrameMarks | undefined): boolean => marks === undefined || (marks.behind.length === 0 && marks.front.length === 0);
  if (empty(a) || empty(b)) {
    return empty(a) && empty(b);
  }
  const cell = Math.min((a as FrameMarks).cell, (b as FrameMarks).cell);
  const left = marksAtCell(a, cell);
  const right = marksAtCell(b, cell);
  const same = (x: ReadonlySet<string>, y: ReadonlySet<string>): boolean => x.size === y.size && [...x].every((key) => y.has(key));
  return same(left.behind, right.behind) && same(left.front, right.front);
}

/**
 * Whether two looks block and open the same wherever the frame is placed and share a ground flag: both without a
 * footprint, or footprints `sameFootprint` finds alike. A frame with no footprint differs from one with an
 * empty footprint, since only the first is filled by a tiles rule (ADR-046).
 *
 * @param a One look.
 * @param b The other.
 * @returns True when they match.
 */
function sameLook(a: FootprintLook, b: FootprintLook): boolean {
  if (a.ground !== b.ground || (a.amongProps ?? false) !== (b.amongProps ?? false) || !sameMarks(a.marks, b.marks)) {
    return false;
  }
  if (a.footprint === undefined || b.footprint === undefined) {
    return a.footprint === b.footprint;
  }
  return sameFootprint(a.footprint, b.footprint);
}

/**
 * The look a copy writes from a source frame: the cells the source blocks drawn as cells, as the library stores
 * them, with the source's passage and surface (ADR-040's amendment of 2026-10-05) and its ground flag and the mark that goes with it.
 *
 * @param source The source frame's look.
 * @param size The source frame's size in pixels.
 * @param cellPixels Pixels per collision cell.
 * @returns The look.
 */
export function copiedLook(source: FootprintLook, size: { readonly w: number; readonly h: number }, cellPixels: number): FootprintLook {
  const blocking = footprintFromCells(source.cells, size.w, size.h, cellPixels);
  const passage = source.footprint?.passage;
  const surface = source.footprint?.surface;
  return { ...source, footprint: passage === undefined || passage.length === 0 ? blocking : { ...blocking, passage, ...(surface === undefined ? {} : { surface }) } };
}

/**
 * The change a copy sends to the library for one frame it writes: the copied cells, ground flag and the mark beside it, and the
 * copied passage as cells with its surface. A copy with no passage names none, so the change holds no field the
 * library did not hold before, unless the frame written holds a passage, which an empty one then removes.
 *
 * @param copied The look the copy writes (`copiedLook`).
 * @param had The footprint the written frame holds, or undefined.
 * @param cellPixels Pixels per collision cell the copied cells are counted in; the change names it as its `cell` when
 *   it is finer than the authoring cell (ADR-113 decision 7).
 * @param hadMarks The marks the written frame holds, or undefined. The copy carries the source's marks counted in
 *   `cellPixels`, and when the source has none and the written frame does, empty lists, which the library takes as
 *   removing them.
 * @returns The change.
 */
export function footprintCopyOverride(copied: FootprintLook, had: PropFootprint | undefined, cellPixels: number, hadMarks?: FrameMarks): Exclude<FootprintOverride, "none"> {
  const opens = copied.footprint === undefined ? [] : footprintOpens(copied.footprint, cellPixels, minCoverForCell(cellPixels));
  const sized = cellPixels < FOOTPRINT_DEFAULT_CELL ? { cell: cellPixels } : {};
  const surface = copied.footprint?.surface;
  const painted = !sameMarks(copied.marks, undefined);
  const held = !sameMarks(hadMarks, undefined);
  const mark = painted ? marksAtCell(copied.marks, cellPixels) : null;
  const marks = mark !== null ? { behind: standCellsOf(mark.behind), front: standCellsOf(mark.front), ...sized } : held ? { behind: [], front: [], ...sized } : {};
  const base = { ...sized, cells: copied.cells.map(([x, y]) => [x, y] as [number, number]), ground: copied.ground, ...(copied.amongProps === undefined ? {} : { amongProps: copied.amongProps }), ...marks };
  if (opens.length === 0) {
    return (had?.passage?.length ?? 0) > 0 ? { ...base, passage: [] } : base;
  }
  return { ...base, passage: opens, ...(surface === undefined ? {} : { surface }) };
}

/**
 * Plans copying one frame's footprint across its strip: every other frame is written, except one that
 * something in the world rests on and whose look differs from the copy, which is kept and named. Looks are
 * compared exactly, by the pixels their footprints cover (`sameFootprint`), so a derived footprint that
 * blocks the copy's cells but covers other pixels is kept (P1.6-T11); since the stamp lays every frame on the
 * cells (P1.6-T78) such a frame blocks the same cells, so the rule is stricter than play needs and only ever
 * keeps a frame it could have written. A frame whose look already matches would gain nothing
 * from the write, so the rule never keeps a frame the copy would leave unchanged.
 *
 * @param world The open world, whose props and states are checked.
 * @param frames The strip's frame ids in order.
 * @param source The frame copied from, one of `frames`.
 * @param copied The look the copy writes.
 * @param lookOf A frame's look now.
 * @returns The frames to write and the frames left, each in strip order.
 * @throws RangeError when the strip does not hold the source frame; Error when a tile cell of the world
 *   indexes past its tilesets.
 */
export function planFootprintCopy(world: WorldDocument, frames: readonly string[], source: string, copied: FootprintLook, lookOf: (frame: string) => FootprintLook): FootprintCopyPlan {
  if (!frames.includes(source)) {
    throw new RangeError(`frame ${source} is not in the strip ${frames[0] ?? "(empty)"} starts`);
  }
  const rests = frameRests(world);
  const write: string[] = [];
  const kept: KeptFrame[] = [];
  frames.forEach((frame, index) => {
    if (frame === source) {
      return;
    }
    const resting = rests.get(frame) ?? [];
    if (resting.length > 0 && !sameLook(lookOf(frame), copied)) {
      kept.push({ frame, index, rests: resting });
    } else {
      write.push(frame);
    }
  });
  return { write, kept };
}
